import { useEffect, useState } from 'react';
import './Login2FA.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import OtpInput from 'react-otp-input';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import loginBanner from '../../assets/images/loginBanner.png';
import { verifyOTP } from '../../store/apis/userService';
import { handlePostLogin } from '../../utils/auth.util';
import ResendOTP from './ResendOTP';
import * as icons from '../../assets';

function Login2FA() {
  document.title = 'Login 2FA | Envision - Daikin';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const rememberMe = queryParams.get('r') === 'true';
  const navigate = useNavigate();
  // Timer state for resend
  const [timer, setTimer] = useState(
    process.env.REACT_APP_OTP_SEND_TIMEOUT || 120,
  );

  useEffect(() => {
    const userId = localStorage.getItem('2FAUserId');
    const email = localStorage.getItem('2FAEmail');
    if (!userId || !email) {
      // Redirect to login page if userId or email is not found
      navigate('/login');
    }
    // Clear the local storage after retrieving the values
  }, [navigate]);

  // Countdown effect
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => setTimer(t => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const { control, handleSubmit, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      userId: localStorage.getItem('2FAUserId'),
      email: localStorage.getItem('2FAEmail'),
      mobile: localStorage.getItem('2FAMobile'),
      otp: '',
    },
  });

  const values = watch();

  // Check if all 6 digits are filled and are numbers
  const allFilled =
    typeof values.otp === 'string' &&
    values.otp.length === 6 &&
    /^[0-9]{6}$/.test(values.otp);

  const {
    mutate: verify,
    isPending,
    isError,
    error,
  } = useMutation({ mutationFn: verifyOTP });

  const onSubmit = form => {
    verify(form, {
      onSuccess: data => {
        localStorage.removeItem('2FAUserId');
        localStorage.removeItem('2FAEmail');
        localStorage.removeItem('2FAMobile');
        // Use the utility function to handle post-login actions
        handlePostLogin(data, '/dashboard', rememberMe);
      },
      onError: err => {
        toast.error(err.message ?? err.error, {
          className: 'toast-error',
        });
      },
    });
  };

  return (
    <div className="login-2fa-container">
      {/* Left Panel */}
      <div className="login-2fa-left">
        <img src={loginBanner} alt="Daikin Banner" className="banner" />
      </div>
      {/* Right Panel */}
      <div className="login-2fa">
        <div className="d-flex justify-content-start flex-column gap-3 w-75">
          <button
            type="button"
            className="back-btn-2fa btn btn-link"
            onClick={() => navigate('/login')}
          >
            <img src={icons.icon2faChevronLeft} alt="Back" />
            <span className="back">Back</span>
          </button>
          <div className="verification-title">Verification</div>
          <div className="verification-subtitle">
            Enter your 6 digits code that you received on your email.
          </div>
        </div>

        <form
          className="w-75 d-flex flex-column gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="otp"
            control={control}
            rules={{
              required: true,
              pattern: /^[0-9]{6}$/,
            }}
            render={({ field }) => (
              <OtpInput
                {...field}
                value={field.value}
                onChange={field.onChange}
                numInputs={6}
                inputType="tel"
                containerStyle={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '20px',
                  width: '100%',
                }}
                inputStyle="otp-input"
                shouldAutoFocus
                renderSeparator={null}
                renderInput={props => (
                  <input
                    {...props}
                    className="otp-input"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    onInput={e => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    }}
                  />
                )}
              />
            )}
          />
          <div className="d-flex flex-row justify-content-center">
            {timer > 0 && (
              <span className="timer-2fa">
                {String(Math.floor(timer / 60)).padStart(2, '0')}:
                {String(timer % 60).padStart(2, '0')}
              </span>
            )}
          </div>
          <button
            type="submit"
            className="verify-btn"
            disabled={!allFilled || isPending}
          >
            {isPending ? 'VERIFYING....' : 'VERIFY'}
          </button>
          <div className="d-flex w-100 align-items-center justify-content-center my-2">
            <ResendOTP setTimer={setTimer} disabled={timer > 0} />
          </div>
        </form>
        {isError && (
          <p className="text-danger my-1">{error.message ?? error.error}</p>
        )}
      </div>
    </div>
  );
}

export default Login2FA;
